import { CheckCircle, Calendar, Users, Phone, Mail, MapPin, ArrowRight, Home, Download, Share2 } from 'lucide-react'
import { BookingResponse } from '../hooks/useBooking'
import { calculatePaymentBreakdown, isPartialPayment, formatRupee } from '../utils/paymentPricing'

interface BookingSuccessProps {
  booking: BookingResponse
  onBackToHome: () => void
  onNewBooking: () => void
}

const BookingSuccess = ({ booking, onBackToHome, onNewBooking }: BookingSuccessProps) => {
  const isPartial = isPartialPayment(booking.payment_method)
  const partialBreakdown = isPartial
    ? calculatePaymentBreakdown(booking.grand_total, booking.payment_method)
    : null
  const amountPaidOnline =
    booking.amount_due_now ?? partialBreakdown?.payNow ?? booking.grand_total
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-pulse">
              <span className="text-white text-sm font-bold">✓</span>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Booking Confirmed! 🎉
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Thank you for choosing AonlaStay
          </p>
          <p className="text-gray-500">
            You will receive a confirmation call shortly
          </p>
        </div>

        {/* Booking Details Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Booking Details</h2>
            <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
              {isPartial
                ? 'PARTIAL PAID'
                : booking.payment_status === 2
                  ? 'CONFIRMED'
                  : booking.payment_status === 0
                    ? 'AWAITING PAYMENT'
                    : booking.payment_status === 1
                      ? 'PENDING'
                      : 'PENDING'}
            </div>
          </div>

          <div className="space-y-6">
            {/* Booking Number */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 font-medium">Booking Number</span>
                <span className="text-lg font-bold text-gray-900 font-mono">
                  {booking.booking_number}
                </span>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                <Calendar className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Check-in</p>
                  <p className="font-semibold text-gray-900">{formatDate(booking.check_in)}</p>
                  <p className="text-xs text-gray-500">After 2:00 PM</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl">
                <Calendar className="h-6 w-6 text-orange-600" />
                <div>
                  <p className="text-sm text-gray-600">Check-out</p>
                  <p className="font-semibold text-gray-900">{formatDate(booking.check_out)}</p>
                  <p className="text-xs text-gray-500">Before 11:00 AM</p>
                </div>
              </div>
            </div>

            {/* Guest Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl">
                <Users className="h-6 w-6 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-600">Guests</p>
                  <p className="font-semibold text-gray-900">{booking.guests} Guest{booking.guests !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl">
                <div className="h-6 w-6 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">₹</span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    {isPartial ? 'Amount paid online' : 'Total paid'}
                  </p>
                  <p className="font-semibold text-gray-900">{formatRupee(amountPaidOnline)}</p>
                </div>
              </div>
            </div>

            {partialBreakdown && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-amber-900">Partial payment summary</p>
                <div className="flex justify-between text-sm text-amber-800">
                  <span>Room total</span>
                  <span className="font-medium">{formatRupee(booking.grand_total)}</span>
                </div>
                <div className="flex justify-between text-sm text-amber-800">
                  <span>Advance + service charge (paid online)</span>
                  <span className="font-medium">{formatRupee(amountPaidOnline)}</span>
                </div>
                <div className="flex justify-between text-sm text-amber-900 font-semibold">
                  <span>Due at check-in</span>
                  <span>{formatRupee(partialBreakdown.dueAtCheckin)}</span>
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      {booking.customer_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">{booking.customer_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Phone className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900">{booking.customer_phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{booking.customer_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-5 h-5 bg-purple-600 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">💳</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment</p>
                    <p className="font-medium text-gray-900">{booking.payment_method}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Important Information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center gap-2">
            <span className="text-xl">📞</span>
            Important Information
          </h3>
          <ul className="space-y-2 text-yellow-700">
            <li className="flex items-start gap-2">
              <span className="text-yellow-600 mt-1">•</span>
              <span>You will receive a confirmation call within 30 minutes</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-600 mt-1">•</span>
              <span>Please keep your booking ID handy for reference</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-600 mt-1">•</span>
              <span>Check-in time is after 2:00 PM, check-out before 11:00 AM</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-600 mt-1">•</span>
              <span>Contact us at +91 9457425914 for any queries</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={onBackToHome}
            className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
          >
            <Home className="h-5 w-5" />
            Back to Home
          </button>
          <button
            onClick={onNewBooking}
            className="flex-1 bg-white border-2 border-primary-500 text-primary-600 py-4 px-6 rounded-xl font-semibold text-lg hover:bg-primary-50 transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
          >
            <ArrowRight className="h-5 w-5" />
            Book Another Room
          </button>
        </div>

        {/* Additional Actions */}
        <div className="flex justify-center gap-4 mt-6">
          <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
            <Download className="h-4 w-4" />
            <span className="text-sm">Download Receipt</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors">
            <Share2 className="h-4 w-4" />
            <span className="text-sm">Share Booking</span>
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>Thank you for choosing AonlaStay for your accommodation needs!</p>
          <p className="mt-1">We look forward to providing you with a comfortable stay.</p>
        </div>
      </div>
    </div>
  )
}

export default BookingSuccess
